import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowLeft, ArrowRight, Pencil } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Modal } from "@/components/ui/modal";

import { Stepper, type StepperStep } from "@/components/ui/stepper";

import { useActionPlan } from "../api/hooks";
import { PlanSettingsForm } from "./PlanSettingsForm";
import { ActionsStep } from "./steps/ActionsStep";
import { IssuesStep } from "./steps/IssuesStep";
import { PlanStep } from "./steps/PlanStep";
import { PrioritizationStep } from "./steps/PrioritizationStep";
import { TrackStep } from "./steps/TrackStep";

const STEP_KEYS = [
  "issues",
  "actions",
  "prioritization",
  "plan",
  "track",
] as const;
type StepKey = (typeof STEP_KEYS)[number];

// Reopening a plan resumes the step the user last visited (persisted per plan
// in localStorage), instead of always resetting to step 1. Purely client-side;
// falls back to the first step when nothing is stored or storage is blocked.
const stepStorageKey = (planId: string) => `activateur.actionPlan.${planId}.step`;

function readStoredStep(planId: string): StepKey {
  try {
    const saved = localStorage.getItem(stepStorageKey(planId));
    if (saved && (STEP_KEYS as readonly string[]).includes(saved)) {
      return saved as StepKey;
    }
  } catch {
    // localStorage unavailable (private mode, etc.) — start at step 1.
  }
  return "issues";
}

/**
 * The single-plan workspace: name/period settings plus the five-step flow
 * (choose issues → courses of action → prioritization → plan actions → track),
 * mirroring the legacy module order. Step state is local; each step reads the
 * same plan-detail query and writes through the action API.
 */
export function PlanWorkspace({
  planId,
  onClose,
}: {
  planId: string;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const plan = useActionPlan(planId);
  const [step, setStepState] = useState<StepKey>(() => readStoredStep(planId));
  const [editingSettings, setEditingSettings] = useState(false);

  const setStep = useCallback(
    (next: StepKey) => {
      setStepState(next);
      try {
        localStorage.setItem(stepStorageKey(planId), next);
      } catch {
        // Persisting the resume point is best-effort; ignore storage failures.
      }
    },
    [planId],
  );

  const steps: StepperStep[] = [
    { key: "issues", label: t("actionPlans.steps.issues") },
    { key: "actions", label: t("actionPlans.steps.actions") },
    { key: "prioritization", label: t("actionPlans.steps.prioritization") },
    { key: "plan", label: t("actionPlans.steps.plan") },
    { key: "track", label: t("actionPlans.steps.track") },
  ];

  return (
    <div className="space-y-6">
      <Button variant="ghost" size="sm" onClick={onClose} className="-ml-2">
        <ArrowLeft />
        {t("actionPlans.nav.backToList")}
      </Button>

      {plan.isLoading && (
        <p className="text-sm text-muted-foreground">{t("actionPlans.loading")}</p>
      )}

      {plan.isError && (
        <Card>
          <CardContent className="flex items-center justify-between gap-4 py-6">
            <p className="text-sm text-destructive">{t("actionPlans.errors.loadFailed")}</p>
            <Button variant="outline" size="sm" onClick={() => void plan.refetch()}>
              {t("actionPlans.errors.retry")}
            </Button>
          </CardContent>
        </Card>
      )}

      {plan.data && (
        <>
          {/* Name + target period live on step 1 (matching the old app's
              "Choix des enjeux" specifications form). Later steps show the name
              read-only with an Edit button that opens the same editor. */}
          <Card>
            {/* CardHeader drops its bottom padding on the assumption a
                CardContent follows; on steps 2+ there is none, so restore it
                here to keep the name vertically centred in the card. */}
            <CardHeader className={step === "issues" ? undefined : "pb-4 sm:pb-6"}>
              <div className="flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
                <CardTitle>
                  {plan.data.name || t("actionPlans.list.untitled")}
                </CardTitle>
                {step !== "issues" && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setEditingSettings(true)}
                  >
                    <Pencil aria-hidden />
                    {t("actionPlans.settings.edit")}
                  </Button>
                )}
              </div>
            </CardHeader>
            {step === "issues" && (
              <CardContent>
                <PlanSettingsForm plan={plan.data} />
              </CardContent>
            )}
          </Card>

          {editingSettings && (
            <Modal
              title={t("actionPlans.settings.title")}
              onClose={() => setEditingSettings(false)}
            >
              <PlanSettingsForm plan={plan.data} />
            </Modal>
          )}

          <div data-tour="ap-progress">
            <Stepper
              steps={steps}
              current={step}
              onSelect={(k) => setStep(k as StepKey)}
              ariaLabel={t("actionPlans.title")}
            />
          </div>

          {step === "issues" && <IssuesStep plan={plan.data} />}
          {step === "actions" && <ActionsStep plan={plan.data} />}
          {step === "prioritization" && <PrioritizationStep plan={plan.data} />}
          {step === "plan" && <PlanStep plan={plan.data} />}
          {step === "track" && <TrackStep plan={plan.data} />}

          <StepNav steps={steps} current={step} onSelect={setStep} />
        </>
      )}
    </div>
  );
}

/**
 * Previous / next step buttons under the step content — the stepper alone left
 * no forward affordance. Each button names its destination step (as the old
 * app's bottom navigation does); the ends of the flow drop the button they
 * don't have.
 */
function StepNav({
  steps,
  current,
  onSelect,
}: {
  steps: StepperStep[];
  current: StepKey;
  onSelect: (key: StepKey) => void;
}) {
  const { t } = useTranslation();
  const index = steps.findIndex((s) => s.key === current);
  const previous = index > 0 ? steps[index - 1] : undefined;
  const next = index < steps.length - 1 ? steps[index + 1] : undefined;

  // The buttons sit at the bottom of a long step, so scroll back up to the new
  // step's heading — the old app got this for free from the page load.
  const go = (key: string) => {
    onSelect(key as StepKey);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="flex flex-col-reverse gap-3 border-t pt-6 sm:flex-row sm:items-center sm:justify-between">
      {previous ? (
        <Button
          variant="outline"
          onClick={() => go(previous.key)}
          aria-label={t("actionPlans.nav.previousStep", { step: previous.label })}
        >
          <ArrowLeft aria-hidden />
          {previous.label}
        </Button>
      ) : (
        <span className="hidden sm:block" />
      )}
      {next && (
        <Button
          onClick={() => go(next.key)}
          aria-label={t("actionPlans.nav.nextStep", { step: next.label })}
        >
          {next.label}
          <ArrowRight aria-hidden />
        </Button>
      )}
    </div>
  );
}
